import React from "react";
import {
  AutoTextarea,
  buttonStyle,
  dangerButtonStyle,
  gcsEyeOptions,
  gcsMotorOptions,
  gcsVerbalOptions,
  getFieldStyle,
  getGcsTotal,
  getLabelStyle,
  inputStyle,
  labelStyle,
  painOptions,
  rhythmOptions,
  SectionCard,
} from "../patientChartShared";

export default function VitalsSection({
  selectedEncounterId,
  vitalsForm = [],
  addVital,
  removeVital,
  setVitalField,
  expandedSections,
  toggleSection,
  sectionRef,
  activeSection,
  readOnlyMode = false,
  errorCount = 0,
}) {
  return (
    <SectionCard
      title="Vitals / Monitoring"
      sectionKey="vitals"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={sectionRef}
      activeSection={activeSection}
      errorCount={errorCount}
      extraHeaderContent={
        selectedEncounterId ? (
          <button
            type="button"
            style={buttonStyle}
            onClick={addVital}
            disabled={readOnlyMode}
          >
            + Add Vitals
          </button>
        ) : null
      }
    >
      {!selectedEncounterId ? (
        <div style={{ color: "#64748b" }}>
          Create or select an encounter first.
        </div>
      ) : !vitalsForm.length ? (
        <div style={{ color: "#64748b" }}>
          No vital signs recorded yet. Click <strong>Add Vitals</strong>.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {vitalsForm.map((vital, index) => {
            const gcsTotal = getGcsTotal(
              vital.gcsEye,
              vital.gcsVerbal,
              vital.gcsMotor
            );
            const bpMissing = !vital.systolic || !vital.diastolic;

            return (
              <div
                key={`${selectedEncounterId}-vital-${index}`}
                style={{
                  border: "1px solid #c7f9f1",
                  borderRadius: 12,
                  padding: 10,
                  background:
                    "linear-gradient(180deg, #f8fffe 0%, #ffffff 100%)",
                  boxShadow: "0 5px 12px rgba(15, 118, 110, 0.05)",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    paddingBottom: 6,
                    borderBottom: "1px solid #ccfbf1",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        color: "#0f766e",
                        fontSize: 14,
                      }}
                    >
                      Vital Set #{index + 1}
                    </div>

                    <label style={{ ...labelStyle, gap: 3, minWidth: 160 }}>
                      <span style={{ fontSize: 11 }}>Date / Time</span>
                      <input
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.time || ""}
                        onChange={(e) =>
                          setVitalField(index, "time", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    style={{
                      ...dangerButtonStyle,
                      padding: "7px 11px",
                      fontSize: 13,
                    }}
                    onClick={() => removeVital(index)}
                    disabled={readOnlyMode}
                  >
                    Remove
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(105px, 1fr))",
                    gap: 8,
                    alignItems: "end",
                  }}
                >
                  <div
                    style={getLabelStyle(
                      bpMissing
                        ? ""
                        : `${vital.systolic}/${vital.diastolic}`,
                      true
                    )}
                  >
                    <span>BP</span>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto 1fr",
                        gap: 5,
                        alignItems: "center",
                      }}
                    >
                      <input
                        style={{
                          ...getFieldStyle(vital.systolic, true),
                          padding: "6px 8px",
                        }}
                        placeholder="SYS"
                        value={vital.systolic || ""}
                        onChange={(e) =>
                          setVitalField(index, "systolic", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <div style={{ fontWeight: 800, color: "#0f766e" }}>
                        /
                      </div>
                      <input
                        style={{
                          ...getFieldStyle(vital.diastolic, true),
                          padding: "6px 8px",
                        }}
                        placeholder="DIA"
                        value={vital.diastolic || ""}
                        onChange={(e) =>
                          setVitalField(index, "diastolic", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                    </div>
                  </div>

                  <div style={getLabelStyle(vital.pulse, true)}>
                    <span>Pulse</span>
                    <div style={{ position: "relative" }}>
                      <input
                        style={{
                          ...getFieldStyle(vital.pulse, true),
                          padding: "6px 40px 6px 8px",
                        }}
                        value={vital.pulse || ""}
                        onChange={(e) =>
                          setVitalField(index, "pulse", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        BPM
                      </span>
                    </div>
                  </div>

                  <div style={getLabelStyle(vital.resp, true)}>
                    <span>Resp</span>
                    <div style={{ position: "relative" }}>
                      <input
                        style={{
                          ...getFieldStyle(vital.resp, true),
                          padding: "6px 42px 6px 8px",
                        }}
                        value={vital.resp || ""}
                        onChange={(e) =>
                          setVitalField(index, "resp", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        /min
                      </span>
                    </div>
                  </div>

                  <div style={labelStyle}>
                    <span>SpO₂</span>
                    <div style={{ position: "relative" }}>
                      <input
                        style={{
                          ...inputStyle,
                          padding: "6px 24px 6px 8px",
                        }}
                        value={vital.spo2 || ""}
                        onChange={(e) =>
                          setVitalField(index, "spo2", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        %
                      </span>
                    </div>
                  </div>

                  <div style={labelStyle}>
                    <span>ETCO₂</span>
                    <div style={{ position: "relative" }}>
                      <input
                        style={{
                          ...inputStyle,
                          padding: "6px 46px 6px 8px",
                        }}
                        value={vital.etco2 || ""}
                        onChange={(e) =>
                          setVitalField(index, "etco2", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        mmHg
                      </span>
                    </div>
                  </div>

                  <div style={labelStyle}>
                    <span>Temp</span>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 66px",
                        gap: 5,
                      }}
                    >
                      <input
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.tempValue || ""}
                        onChange={(e) =>
                          setVitalField(index, "tempValue", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                      <select
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.tempUnit || "F"}
                        onChange={(e) =>
                          setVitalField(index, "tempUnit", e.target.value)
                        }
                        disabled={readOnlyMode}
                      >
                        <option value="F">°F</option>
                        <option value="C">°C</option>
                      </select>
                    </div>
                  </div>

                  <label style={labelStyle}>
                    <span>Pain</span>
                    <select
                      style={{ ...inputStyle, padding: "6px 8px" }}
                      value={vital.pain || ""}
                      onChange={(e) =>
                        setVitalField(index, "pain", e.target.value)
                      }
                      disabled={readOnlyMode}
                    >
                      {painOptions.map((option) => (
                        <option key={option || "blank"} value={option}>
                          {option || "Select"}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={labelStyle}>
                    <span>EKG</span>
                    <select
                      style={{ ...inputStyle, padding: "6px 8px" }}
                      value={vital.rhythm || ""}
                      onChange={(e) =>
                        setVitalField(index, "rhythm", e.target.value)
                      }
                      disabled={readOnlyMode}
                    >
                      <option value="">Select</option>
                      {rhythmOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  {vital.rhythm === "Other" && (
                    <label style={labelStyle}>
                      <span>Other Rhythm</span>
                      <input
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        placeholder="Enter rhythm"
                        value={vital.rhythmOther || ""}
                        onChange={(e) =>
                          setVitalField(index, "rhythmOther", e.target.value)
                        }
                        disabled={readOnlyMode}
                      />
                    </label>
                  )}
                </div>

                <div
                  style={{
                    border: "1px solid #ccfbf1",
                    borderRadius: 10,
                    background: "#f0fdfa",
                    padding: 8,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#115e59",
                        fontSize: 14,
                      }}
                    >
                      Glasgow Coma Scale
                    </div>

                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "5px 9px",
                        borderRadius: 999,
                        background: "#ffffff",
                        border: "1px solid #bae6fd",
                      }}
                    >
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        Total
                      </span>
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#0f766e",
                        }}
                      >
                        {gcsTotal || "—"}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: 8,
                    }}
                  >
                    <label style={labelStyle}>
                      <span>Eye</span>
                      <select
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.gcsEye || ""}
                        onChange={(e) =>
                          setVitalField(index, "gcsEye", e.target.value)
                        }
                        disabled={readOnlyMode}
                      >
                        {gcsEyeOptions.map((option) => (
                          <option key={option.label} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label style={labelStyle}>
                      <span>Verbal</span>
                      <select
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.gcsVerbal || ""}
                        onChange={(e) =>
                          setVitalField(index, "gcsVerbal", e.target.value)
                        }
                        disabled={readOnlyMode}
                      >
                        {gcsVerbalOptions.map((option) => (
                          <option key={option.label} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label style={labelStyle}>
                      <span>Motor</span>
                      <select
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        value={vital.gcsMotor || ""}
                        onChange={(e) =>
                          setVitalField(index, "gcsMotor", e.target.value)
                        }
                        disabled={readOnlyMode}
                      >
                        {gcsMotorOptions.map((option) => (
                          <option key={option.label} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                <label style={labelStyle}>
                  <span>Notes</span>
                  <AutoTextarea
                    value={vital.notes || ""}
                    onChange={(e) =>
                      setVitalField(index, "notes", e.target.value)
                    }
                    style={{ minHeight: 56 }}
                    disabled={readOnlyMode}
                  />
                </label>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}